import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  subWeeks,
  isAfter,
  startOfDay,
} from 'date-fns';
import locationDashboardService from './locationDashboardService';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const weekDaysMondayStart = (referenceDate = new Date()) => {
  const start = startOfWeek(referenceDate, { weekStartsOn: 1 });
  return eachDayOfInterval({ start, end: endOfWeek(start, { weekStartsOn: 1 }) });
};

const fetchDailySales = async (dateStr) => {
  try {
    const d = await locationDashboardService.getSalesReport(dateStr, dateStr);
    return Number(d.totalSales ?? d.totalRevenue ?? 0);
  } catch {
    return 0;
  }
};

const sumWeekSales = async (days) => {
  const amounts = await Promise.all(days.map((day) => fetchDailySales(format(day, 'yyyy-MM-dd'))));
  return amounts.reduce((sum, v) => sum + v, 0);
};

/** Monthly series — existing dashboard API. */
export const fetchMonthlySalesOverview = async () => {
  const [monthlyRes, performanceRes] = await Promise.all([
    locationDashboardService.getMonthlySales().catch(() => ({ data: [], labels: [] })),
    locationDashboardService.getPerformanceMetrics().catch(() => ({})),
  ]);

  const data = (monthlyRes.data || []).map((v) => Number(v) || 0);
  const labels = monthlyRes.labels || [];
  const thisMonth = data.length ? data[data.length - 1] : 0;
  const lastMonth = data.length >= 2 ? data[data.length - 2] : 0;
  const dailyAverage = Number(performanceRes.averageDailySales ?? 0);

  return {
    labels,
    data,
    metrics: {
      primary: thisMonth,
      secondary: lastMonth,
      dailyAverage,
    },
  };
};

/** Current week Mon–Sun chart + this week / last week totals. */
export const fetchWeeklySalesOverview = async () => {
  const today = startOfDay(new Date());
  const thisWeekDays = weekDaysMondayStart(today);
  const lastWeekDays = weekDaysMondayStart(subWeeks(today, 1));

  const [thisWeekAmounts, lastWeekTotal] = await Promise.all([
    Promise.all(
      thisWeekDays.map(async (day) => {
        if (isAfter(day, today)) {
          return 0;
        }
        return fetchDailySales(format(day, 'yyyy-MM-dd'));
      })
    ),
    sumWeekSales(lastWeekDays),
  ]);

  const labels = WEEKDAY_LABELS;
  const data = thisWeekAmounts;
  const thisWeekTotal = thisWeekAmounts.reduce((sum, v) => sum + v, 0);
  const daysWithData = thisWeekDays.filter((day) => !isAfter(day, today)).length || 1;
  const dailyAverage = thisWeekTotal / daysWithData;

  return {
    labels,
    data,
    metrics: {
      primary: thisWeekTotal,
      secondary: lastWeekTotal,
      dailyAverage,
    },
  };
};

export const fetchSalesOverviewByPeriod = (period) =>
  period === 'weekly' ? fetchWeeklySalesOverview() : fetchMonthlySalesOverview();
